import { Injectable } from '@angular/core'
import { Observable, Subject } from 'rxjs'
import {
  ColumnTypeOverride,
  ColumnTypeOverrideMap,
  DEFAULT_COLUMN_TYPE_OVERRIDE,
  isColumnTypeOverride,
} from '../../models/column-type-override.model'

/**
 * The ONE store for per-column comparison-type overrides, shared by every panel that has untyped
 * (AGGREGATE_INFO) columns.
 *
 * Deliberately `providedIn: 'root'`, not panel-scoped: PVAL-26423 requires the override to live in
 * a single place consumed by BOTH positions-2 and transactions-2, rather than each panel growing its
 * own copy inside its own `aggregate-info.service.ts`. It lives in `libs/filter-modal` because that
 * is where the picker control that writes it lives, and because both panels already depend on this
 * library for their filter surfaces.
 *
 * Two surfaces write here — the filter modal's editor row and the grid header's custom filter — and
 * they must agree, which is the other reason this is not per-surface state.
 *
 * ## Scoping
 *
 * A `scope` is an opaque caller-chosen key identifying "which grid's columns are these". Callers
 * should make it as specific as the column identity is: positions-2 uses one scope per
 * panel+source, because the same `field` string can mean an untyped Prometheus key in one source and
 * nothing at all in another. Nothing here parses a scope.
 *
 * ## Persistence
 *
 * This service holds the SESSION's state and nothing else — it does not talk to a backend. Saved
 * views (PVAL-26418) persist the override by snapshotting {@link overridesFor} and handing it back
 * through {@link restore} on load, which is why both are part of the public surface.
 */
@Injectable({ providedIn: 'root' })
export class ColumnTypeOverrideService {
  private readonly overrides = new Map<
    string,
    Map<string, ColumnTypeOverride>
  >()

  private readonly changesSubject = new Subject<ColumnTypeOverrideChange>()

  /**
   * Emits the scope (and the affected fields) whose overrides just changed. Consumers re-read
   * through {@link typeFor} rather than trusting a payload value, so a listener can never act on a
   * half-applied {@link restore}.
   */
  get changes$(): Observable<ColumnTypeOverrideChange> {
    return this.changesSubject.asObservable()
  }

  /**
   * The user's chosen type for a column, or `undefined` when they have expressed no preference.
   *
   * `undefined` is distinct from `'text'` on purpose: a column the user has never touched must not
   * be reported to a saved view as an explicit Text choice, and the 400 reset-to-Text path needs to
   * be able to CLEAR an override rather than pin it to Text (see {@link clear}).
   */
  typeFor(scope: string, field: string): ColumnTypeOverride | undefined {
    return this.overrides.get(scope)?.get(field)
  }

  /** The chosen type, falling back to the catalog-equivalent default. */
  resolvedTypeFor(scope: string, field: string): ColumnTypeOverride {
    return this.typeFor(scope, field) ?? DEFAULT_COLUMN_TYPE_OVERRIDE
  }

  /**
   * Record the user's chosen type for a column — but only when it actually differs from what is
   * already in effect.
   *
   * Compares against {@link resolvedTypeFor} (the EFFECTIVE type), not {@link typeFor} (the explicit
   * one): a caller — the Filters modal commits every column carrying a `typeOverride` context on
   * Apply, not just the ones the user actually picked a type for, and the grid-header filter's own
   * `apply()` fires on every predicate Apply whether or not the picker was touched — always passes the
   * CURRENTLY-RESOLVED type for a column the user never touched (the catalog-equivalent default,
   * since that is what the picker shows). Guarding on `typeFor` alone would let that pass through as a
   * genuine write (`undefined` vs the resolved default are never `===`), pinning an explicit choice
   * nobody made and breaking the very distinction {@link typeFor}'s own doc comment describes.
   *
   * `fromOwnFilterPopover` is true ONLY when the caller IS `field`'s own grid-header filter instance,
   * mid-`apply()` — see {@link ColumnTypeOverrideChange.skipDestroy}. Every other caller (the Filters
   * modal, a future saved-view restore) leaves it false, which is what lets a change made from the
   * modal still force that column's own (possibly stale, possibly not even open) header popover to be
   * destroyed and rebuilt against the new type.
   */
  setType(
    scope: string,
    field: string,
    type: ColumnTypeOverride,
    fromOwnFilterPopover = false,
  ): void {
    if (this.resolvedTypeFor(scope, field) === type) return
    const forScope =
      this.overrides.get(scope) ?? new Map<string, ColumnTypeOverride>()
    forScope.set(field, type)
    this.overrides.set(scope, forScope)
    this.changesSubject.next({
      scope,
      fields: [field],
      skipDestroy: fromOwnFilterPopover ? [field] : [],
    })
  }

  /**
   * Drop the overrides for `fields`, returning them to the catalog default (Text).
   *
   * Variadic over fields because its one real caller is the 400 INVALID_FIELD_TYPE reset: Postgres
   * reports the offending VALUE, not the column, so the error names EVERY field that claimed the
   * failing type and all of them have to be reset together — resetting one at a time would leave
   * the retry able to fail identically.
   *
   * Emits exactly the fields ACTUALLY removed, not the whole scope: a caller that reacts by
   * refreshing/destroying stale filter instances must touch only the columns this call really
   * changed, or a predicate on some OTHER, unrelated AGG_INFO column gets silently evicted alongside
   * the ones this call was asked to reset. Never self-initiated (see {@link setType}), so nothing here
   * needs excluding from that refresh.
   *
   * Returns whether it actually emitted. `fields` can legitimately name nothing this store holds — an
   * empty `fields` list (a 400 body whose own `fields` was not a list), or a field the 400 named whose
   * catalog type was already trustworthy (not AGG_INFO, so it never had an override to begin with) —
   * and a caller whose own re-query is conditional on `changes$` firing needs to know that so it can
   * still re-run one itself.
   */
  clear(scope: string, fields: readonly string[]): boolean {
    const forScope = this.overrides.get(scope)
    if (!forScope) return false
    const removed = fields.filter(field => forScope.delete(field))
    if (removed.length === 0) return false
    this.changesSubject.next({ scope, fields: removed, skipDestroy: [] })
    return true
  }

  /** Every override held for a scope, as a plain object a saved view can persist. */
  overridesFor(scope: string): ColumnTypeOverrideMap {
    const forScope = this.overrides.get(scope)
    if (!forScope) return {}
    return Object.fromEntries(forScope)
  }

  /**
   * Replace a scope's overrides wholesale, e.g. when a saved view loads.
   *
   * Unrecognised values are dropped rather than trusted: the map arrives from persisted JSON that a
   * previous (or future) release wrote, and letting an unknown string through would put it straight
   * onto a request's `filterType` where the backend would reject it.
   *
   * Emits `fields: null` — a wildcard, unlike {@link setType}/{@link clear}'s explicit lists — because
   * a wholesale replace can touch any column the new map names, and enumerating every prior AND new
   * key here is no safer than telling the listener to just refresh everything for the scope.
   */
  restore(scope: string, overrides: ColumnTypeOverrideMap): void {
    const forScope = new Map<string, ColumnTypeOverride>()
    Object.entries(overrides ?? {}).forEach(([field, type]) => {
      if (isColumnTypeOverride(type)) forScope.set(field, type)
    })
    this.overrides.set(scope, forScope)
    this.changesSubject.next({ scope, fields: null, skipDestroy: [] })
  }
}

/**
 * A change to a scope's overrides.
 *
 * `fields` names every column that changed, or `null` for a wildcard bulk replace ({@link
 * ColumnTypeOverrideService.restore}) with no fixed list to enumerate.
 *
 * `skipDestroy` names fields, among `fields`, whose own live filter instance must NOT be destroyed by
 * a listener that otherwise refreshes stale instances after a change (e.g. to pick up a new
 * `filterParams.columnTypeOverride.type`). Populated only by {@link ColumnTypeOverrideService.setType}
 * when the caller IS that field's own grid-header filter, mid-`apply()`: destroying it there would
 * blank the condition the user just entered (ag-Grid disposes a filter via `setModel(null)`) before
 * the caller gets a chance to read it back out via `getModel()`. Always empty for a bulk change
 * (`clear`, `restore`), which never originates from inside a live filter's own commit.
 */
export interface ColumnTypeOverrideChange {
  scope: string
  fields: readonly string[] | null
  skipDestroy: readonly string[]
}
